/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react'
import styled from 'styled-components'

import ParseContent from 'components/shared/ParseContent'

import ChevronRight from 'img/chevron.inline.svg'

const Section = styled.section`
  background: ${({ theme }) => theme.color.grey};
  padding: 2.5rem 0;

  @media (min-width: 992px) {
    padding: 3.5rem 0;
  }
`

const Row = styled.div`
  align-items: stretch;

  @media (min-width: 992px) {
    min-height: 420px;
  }
`

const LeftColumn = styled.div`
  position: relative;
  padding: 1rem 0 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;

  @media (min-width: 992px) {
    padding: 2rem 1.5rem 2rem 0;
  }
`

const BackgroundTitle = styled.p`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0;
  text-align: center;
  font-family: ${({ theme }) => theme.font.family.primary};
  font-weight: ${({ theme }) => theme.font.weight.extraBold};
  font-size: clamp(3rem, 11vw, 7.5rem);
  line-height: 0.95;
  letter-spacing: -0.03em;
  color: #18263f;
  opacity: 0.07;
  z-index: 0;

  @media (min-width: 992px) {
    font-size: 140px;
  }
`

const QuoteBox = styled.div`
  position: relative;
  z-index: 1;
  overflow: visible;
  background-color: #18263f;
  border-radius: 0;
  margin-top: 5.5rem;
  padding: 3rem 2.25rem;
  width: 100%;
  max-width: 550px;

  @media (max-width: 991.98px) {
    margin-top: 2.5rem;
  }

  @media (min-width: 992px) {
    padding: 2.25rem 1.75rem 2rem 1.75rem;
  }
`

const QuoteMarkWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0.5rem;
  z-index: 2;
  width: clamp(56px, 14vw, 76px);
  transform: translateY(-50%);
  pointer-events: none;
  line-height: 0;

  & svg {
    width: 100%;
    height: auto;
    display: block;
    vertical-align: top;
  }
`

let quoteFilterSeq = 0

/** Decorative quotation mark (design asset); filter id must be unique per instance. */
const QuoteMarkSvg: React.FC = () => {
  const filterId = useMemo(() => {
    quoteFilterSeq += 1
    return `quote-filter-${quoteFilterSeq}`
  }, [])
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={76}
      height={59}
      viewBox="0 0 76 59"
      fill="none"
      aria-hidden
    >
      <g filter={`url(#${filterId})`}>
        <path
          d="M22.5012 0C11.1408 7.99373 4 19.3448 4 32.4546C4 44.1254 11.79 51 20.5537 51C28.6683 51 35.4845 44.2853 35.4845 36.1317C35.4845 28.2978 29.8043 23.0219 22.9881 23.0219C21.3652 23.0219 19.9045 23.3417 19.2554 23.5016C20.8783 17.7461 27.6945 10.8715 32.8878 8.31348L22.5012 0ZM59.0167 0C47.6563 7.99373 40.5155 19.3448 40.5155 32.4546C40.5155 44.1254 48.4678 51 57.2315 51C65.1838 51 72 44.2853 72 36.1317C72 28.2978 66.4821 23.0219 59.5036 23.0219C57.8807 23.0219 56.42 23.3417 55.7709 23.5016C57.5561 17.7461 64.21 10.8715 69.4033 8.31348L59.0167 0Z"
          fill="#FFCD0A"
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="76"
          height="59"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_2913_295"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_2913_295"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  )
}

const QuoteContent = styled(ParseContent)`
  position: relative;
  z-index: 1;
  text-align: left;
  color: ${({ theme }) => theme.color.light};

  & p {
    margin: 0;
    text-align: left;
    font-weight: ${({ theme }) => theme.font.weight.extraBold};
    font-family: ${({ theme }) => theme.font.family.primary};
    font-size: 36px;
    line-height: normal;

    @media (max-width: 991.98px) {
      font-size: 24px;
    }
  }

  & strong,
  & b {
    font-weight: ${({ theme }) => theme.font.weight.extraBold};
  }
`

const CtaRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  position: relative;
  z-index: 1;
`

const CtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: ${({ theme }) => theme.color.secondary};
  color: ${({ theme }) => theme.color.dark};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  text-transform: uppercase;
  text-decoration: none !important;
  padding: 0.65rem 1.1rem;
  font-size: 14px;
  letter-spacing: 0.02em;

  @media (min-width: 992px) {
    font-size: 15px;
    padding: 0.7rem 1.25rem;
  }

  &:hover {
    color: ${({ theme }) => theme.color.dark};
    filter: brightness(0.97);
  }

  & svg {
    width: 0.55em;
    height: auto;
    flex-shrink: 0;
  }
`

const VideoColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;

  @media (min-width: 992px) {
    justify-content: flex-end;
    padding: 0 0 0 1rem;
  }
`

const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;

  @media (min-width: 576px) {
    max-width: 340px;
  }

  @media (min-width: 992px) {
    max-width: 380px;
  }
`

/** Portrait 9:16 — padding % is relative to width */
const AspectInner = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 177.78%;
  overflow: hidden;
  background: ${({ theme }) => theme.color.dark};

  & iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`

const VideoOverlayTitle = styled.div`
  position: absolute;
  bottom: 0.65rem;
  left: 0.5rem;
  right: 0.5rem;
  text-align: center;
  z-index: 2;
  pointer-events: none;

  & span {
    display: inline-block;
    color: ${({ theme }) => theme.color.secondary};
    font-family: ${({ theme }) => theme.font.family.primary};
    font-weight: ${({ theme }) => theme.font.weight.extraBold};
    font-size: clamp(0.875rem, 2.8vw, 1.25rem);
    line-height: 1.2;
    text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000,
      1px 1px 0 #000, 0 0 4px rgba(0, 0, 0, 0.4);
  }
`

interface VideoVacancyProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_VideoVacancy
}

const VideoVacancy: React.FC<VideoVacancyProps> = ({ fields }) => {
  const videos = fields?.videos?.filter((v) => v?.url) ?? []
  const primary = videos[0]

  if (!primary?.url) {
    return null
  }

  return (
    <Section className="pb-lg-5">
      <div className="container">
        <Row className="row">
          <div className="col-lg-8">
            <LeftColumn>
              <BackgroundTitle aria-hidden>{fields?.title}</BackgroundTitle>
              {fields?.description && (
                <QuoteBox>
                  <QuoteMarkWrap>
                    <QuoteMarkSvg />
                  </QuoteMarkWrap>
                  <QuoteContent content={fields.description} />
                </QuoteBox>
              )}
              <CtaRow>
                <CtaLink href="https://vimeo.com/1179197103" target="_blank">
                  Bekijk video
                  <ChevronRight aria-hidden />
                </CtaLink>
              </CtaRow>
            </LeftColumn>
          </div>
          <div className="col-lg-4">
            <VideoColumn>
              <VideoFrame id="video-vacancy-player">
                <AspectInner>
                  <iframe
                    title={primary.title || 'Video'}
                    src={primary.url}
                    allowFullScreen
                    loading="lazy"
                  />
                </AspectInner>
                {primary.title && (
                  <VideoOverlayTitle>
                    <span>{primary.title}</span>
                  </VideoOverlayTitle>
                )}
              </VideoFrame>
            </VideoColumn>
          </div>
        </Row>
      </div>
    </Section>
  )
}

export default VideoVacancy
